import React, {useEffect, useRef, useState} from "react";
import {useDrag} from "react-dnd";

// Import slice - PATH RELATIVI!
import {OdPLegacy} from "../produzione/odpLegacySlice";
import {Ostacolo, OSTACOLO_ICON_MAP} from "../ostacoli/ostacoliSlice";

// D&D types
import {ODP_DROP_TYPE, OdPDragItem} from "./gantt/schedulazioneGanttTypes";

// Componenti locali
import OdPSidebarFasi from "./OdPSidebarFasi";
import OstacoloForm from "../ostacoli/OstacoloForm";

// Material UI
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";

// Icons
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import BlockIcon from "@mui/icons-material/Block";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import {theme} from "../../theme";
import {DateTime} from "luxon";

// ============================================
// COSTANTI
// ============================================

const getStatoColor = (
    stato?: number
): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (stato) {
        case 0:
            return "default";
        case 1:
            return "info";
        case 2:
            return "primary";
        case 3:
            return "warning";
        case 4:
            return "success";
        default:
            return "default";
    }
};

const getOstacoloColor = (ostacolo: Ostacolo) => {
    if (ostacolo.risolto) return theme.palette.success.main;
    if (ostacolo.is_scaduto) return theme.palette.error.main;
    return theme.palette.warning.main;
};

// ============================================
// PROPS
// ============================================

interface OdPSidebarCardProps {
    odp: OdPLegacy;
    isAttivo?: boolean;
    onSelect?: (odpId: number | undefined) => void;
    onNavigaAFrazione?: (dataInizio: string) => void;
    onOstacoloChange?: () => void;
}

// ============================================
// COMPONENT
// ============================================

const OdPSidebarCard = ({odp, isAttivo, onSelect, onNavigaAFrazione, onOstacoloChange}: OdPSidebarCardProps) => {
    const [expanded, setExpanded] = useState(false);
    const [ostacoloDialogOpen, setOstacoloDialogOpen] = useState(false);
    const [ostacoloSelezionatoId, setOstacoloSelezionatoId] = useState<number | undefined>(undefined);
    const ref = useRef<HTMLDivElement>(null);

    // D&D: rende la card draggabile verso il Gantt
    const dragItem: OdPDragItem = {
        type: ODP_DROP_TYPE,
        odpId: odp.id!,
        idDocumento: odp.id_documento,
        codiceArticolo: odp.codice_articolo,
        nomeCliente: odp.nome_cliente,
    };

    const [{isDragging}, drag] = useDrag(
        () => ({
            type: ODP_DROP_TYPE,
            item: dragItem,
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
        }),
        [dragItem]
    );

    drag(ref);

    // Quando la card diventa attiva (es. selezione di una fase nel Gantt),
    // portala automaticamente in vista nello scroll della spalla.
    useEffect(() => {
        if (isAttivo && ref.current) {
            ref.current.scrollIntoView({behavior: "smooth", block: "nearest"});
        }
    }, [isAttivo]);

    // ============================================
    // RENDER
    // ============================================

    const percSchedulazione = odp.percentuale_schedulazione ?? 0;
    const schedColor =
        percSchedulazione >= 100
            ? theme.palette.success.main
            : percSchedulazione <= 0
                ? theme.palette.error.main
                : theme.palette.warning.main;

    const dataFineDt = DateTime.fromISO(odp.data_fine_pianificata || "");

    return (
        <>
        <Card
            ref={ref}
            onClick={() => onSelect?.(isAttivo ? undefined : odp.id)}
            sx={{
                mb: 0.5,
                cursor: "grab",
                opacity: isDragging ? 0.4 : 1,
                transition: "box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out",
                border: isAttivo
                    ? `2px solid ${theme.palette.primary.main}`
                    : `2px solid transparent`,
                backgroundColor: isAttivo
                    ? theme.palette.action.selected
                    : undefined,
                "&:hover": {
                    boxShadow: theme.shadows[3],
                },
            }}
        >
            <CardContent sx={{py: 0.5, px: 1, "&:last-child": {pb: 0.5}}}>
                {/* Riga 1: codice_articolo + documento + sched% + expand */}
                <Stack
                    direction="row"
                    sx={{alignItems: "center"}}
                >
                    <Typography variant="caption" fontWeight="bold" color="primary" sx={{width: "35%"}} noWrap>
                        {odp.codice_articolo}
                    </Typography>
                    <Typography variant="caption" color="primary" sx={{width: "30%"}} noWrap>
                        {odp.esercizio}/{odp.tipo_documento}/{odp.numero_registrazione}
                    </Typography>
                    <Typography
                        variant="caption"
                        fontWeight="bold"
                        sx={{color: schedColor, width: "25%", textAlign: "right"}}
                        noWrap
                    >
                        {`sched.:${
                            new Intl.NumberFormat("it-IT", {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2,
                            }).format(percSchedulazione)
                        }%`}
                    </Typography>
                    <Box sx={{width: "10%", textAlign: "right"}}>
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                setExpanded(!expanded);
                            }}
                            size="small"
                            sx={{p: 0}}
                        >
                            {expanded ? (
                                <ExpandLessIcon sx={{fontSize: 16}}/>
                            ) : (
                                <ExpandMoreIcon sx={{fontSize: 16}}/>
                            )}
                        </IconButton>
                    </Box>
                </Stack>

                {/* Riga 2: cliente + quantità + stato */}
                <Stack
                    direction="row"
                    sx={{alignItems: "center"}}
                >
                    <Typography variant="caption" noWrap color="text.secondary" sx={{width: "50%"}}>
                        {odp.nome_cliente || "-"}
                    </Typography>
                    <Tooltip
                        title={`Lavorata: ${Intl.NumberFormat("it-IT", {maximumFractionDigits: 0, minimumFractionDigits: 0}).format(odp.quantita_lavorata ?? 0)} | Residua: ${Intl.NumberFormat("it-IT", {maximumFractionDigits: 0, minimumFractionDigits: 0}).format(odp.quantita_residua ?? 0)}`}
                    >
                        <Typography variant="caption" fontWeight="bold" sx={{width: "15%", textAlign: "right"}}>
                            {Intl.NumberFormat("it-IT", {maximumFractionDigits: 0, minimumFractionDigits: 0}).format(odp.quantita ?? 0)}
                        </Typography>
                    </Tooltip>
                    <Box sx={{width: "35%", textAlign: "right"}}>
                        <Chip
                            label={odp.stato_display || "-"}
                            color={getStatoColor(odp.stato)}
                            size="small"
                            sx={{height: 18, fontSize: "0.65rem"}}
                        />
                    </Box>
                </Stack>

                {/* Riga ostacoli: chip esistenti + bottone aggiungi */}
                {/*<Stack direction="row" spacing={0.25} sx={{alignItems: "center", flexWrap: "wrap", gap: 0.25, mt: 0.25}}>*/}
                {/*    {odp.ostacoli?.map((ostacolo) => (*/}
                {/*        <Tooltip*/}
                {/*            key={ostacolo.id}*/}
                {/*            title={*/}
                {/*                `${ostacolo.tipo_display}: ${ostacolo.descrizione || "-"}` +*/}
                {/*                (ostacolo.data_prevista_risoluzione*/}
                {/*                    ? ` — Vincolo: ${DateTime.fromISO(ostacolo.data_prevista_risoluzione).toFormat("dd/MM/yyyy")}`*/}
                {/*                    : "") +*/}
                {/*                (ostacolo.is_scaduto*/}
                {/*                    ? ` (scaduto ${ostacolo.giorni_ritardo}gg)`*/}
                {/*                    : "")*/}
                {/*            }*/}
                {/*        >*/}
                {/*            <Chip*/}
                {/*                icon={<BlockIcon sx={{fontSize: 12}} />}*/}
                {/*                label={ostacolo.tipo || ""}*/}
                {/*                size="small"*/}
                {/*                color={*/}
                {/*                    ostacolo.risolto*/}
                {/*                        ? "success"*/}
                {/*                        : ostacolo.is_scaduto*/}
                {/*                            ? "error"*/}
                {/*                            : "warning"*/}
                {/*                }*/}
                {/*                variant={ostacolo.risolto ? "outlined" : "filled"}*/}
                {/*                sx={{height: 18, fontSize: "0.6rem", cursor: "pointer"}}*/}
                {/*                onClick={(e) => {*/}
                {/*                    e.stopPropagation();*/}
                {/*                    setOstacoloSelezionatoId(ostacolo.id);*/}
                {/*                    setOstacoloDialogOpen(true);*/}
                {/*                }}*/}
                {/*            />*/}
                {/*        </Tooltip>*/}
                {/*    ))}*/}
                {/*    <Tooltip title="Aggiungi ostacolo">*/}
                {/*        <IconButton*/}
                {/*            onClick={(e) => {*/}
                {/*                e.stopPropagation();*/}
                {/*                setOstacoloSelezionatoId(undefined);*/}
                {/*                setOstacoloDialogOpen(true);*/}
                {/*            }}*/}
                {/*            size="small"*/}
                {/*            sx={{p: 0}}*/}
                {/*        >*/}
                {/*            <AddCircleOutlineIcon*/}
                {/*                sx={{fontSize: 16}}*/}
                {/*                color={odp.ostacoli?.some((o) => !o.risolto) ? "error" : "action"}*/}
                {/*            />*/}
                {/*        </IconButton>*/}
                {/*    </Tooltip>*/}
                {/*</Stack>*/}
                {/* Riga 3+: ostacoli (sinistra) + frazioni (centro) + data fine (destra) */}
                <Stack
                    direction="row"
                    sx={{alignItems: "flex-start", mt: 1}}
                >
                    {/* Sinistra: ostacoli + bottone aggiungi */}
                    <Box sx={{flexShrink: 0}}>
                        {odp.ostacoli?.map((ostacolo) => {
                            const IconComp = OSTACOLO_ICON_MAP[ostacolo.tipo || "ALT"] || HelpOutlineIcon;
                            const color = getOstacoloColor(ostacolo);
                            return (
                                <Tooltip
                                    key={ostacolo.id}
                                    title={
                                        `${ostacolo.tipo_display}: ${ostacolo.descrizione || "-"}` +
                                        (ostacolo.is_scaduto
                                            ? ` (scaduto ${ostacolo.giorni_ritardo}gg)`
                                            : "")
                                    }
                                >
                                    <Stack
                                        direction="row"
                                        spacing={0.25}
                                        sx={{
                                            alignItems: "center",
                                            cursor: "pointer",
                                            py: 0.1,
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setOstacoloSelezionatoId(ostacolo.id);
                                            setOstacoloDialogOpen(true);
                                        }}
                                    >
                                        <IconComp sx={{fontSize: 14, color}} />
                                        <Typography variant="caption" sx={{color, fontSize: "0.65rem"}}>
                                            {ostacolo.data_prevista_risoluzione
                                                ? DateTime.fromISO(ostacolo.data_prevista_risoluzione).toFormat("dd/MM/yyyy")
                                                : "-"}
                                        </Typography>
                                    </Stack>
                                </Tooltip>
                            );
                        })}
                        <Tooltip title="Aggiungi ostacolo">
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOstacoloSelezionatoId(undefined);
                                    setOstacoloDialogOpen(true);
                                }}
                                size="small"
                                sx={{p: 0}}
                            >
                                <AddCircleOutlineIcon
                                    sx={{fontSize: 16}}
                                    color="action"
                                />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {/* Centro: chip frazioni */}
                    <Box sx={{flexGrow: 1, display: "flex", flexWrap: "wrap", gap: 0.25, justifyContent: "center"}}>
                        {odp.frazioni && odp.frazioni.length > 0 && odp.frazioni.map((f) => {
                            const dt = DateTime.fromISO(f.data_inizio_pianificata);
                            return (
                                <Chip
                                    key={f.id}
                                    label={dt.isValid ? dt.toFormat("dd/MM") : "??"}
                                    size="small"
                                    color={dt.isValid ? "primary" : "error"}
                                    variant="outlined"
                                    sx={{height: 16, fontSize: "0.55rem", cursor: dt.isValid ? "pointer" : "default"}}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (dt.isValid) {
                                            onNavigaAFrazione?.(f.data_inizio_pianificata);
                                        }
                                    }}
                                />
                            );
                        })}
                    </Box>

                    {/* Destra: data fine pianificata */}
                    <Typography variant="caption" fontWeight="bold" color="primary" sx={{whiteSpace: "nowrap", flexShrink: 0}}>
                        {DateTime.fromISO(odp.data_fine_pianificata || "").toFormat("dd/MM/yyyy")}
                    </Typography>
                </Stack>
            </CardContent>

            {/* Fasi espandibili */}
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Box
                    sx={{
                        backgroundColor: theme.palette.grey[50],
                        borderTop: `1px solid ${theme.palette.divider}`,
                        px: 0.5,
                        py: 0.5,
                    }}
                >
                    <OdPSidebarFasi fasi={odp.fasi || []} odpId={odp.id!}/>
                </Box>
            </Collapse>
        </Card>

        {/* Dialog ostacolo */}
        <Dialog
            open={ostacoloDialogOpen}
            onClose={() => setOstacoloDialogOpen(false)}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                {ostacoloSelezionatoId ? "Modifica Ostacolo" : "Nuovo Ostacolo"}
            </DialogTitle>
            <DialogContent>
                <OstacoloForm
                    ostacoloId={ostacoloSelezionatoId}
                    odpId={odp.id}
                    onClose={() => setOstacoloDialogOpen(false)}
                    onSaved={onOstacoloChange}
                />
            </DialogContent>
        </Dialog>
        </>
    );
};

export default OdPSidebarCard;